import { useMemo, useState } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { STRIPE_LINKS } from '../../../lib/stripe';
import CalculatorLayout from '../../../components/calculator/CalculatorLayout';
import NumberInput from '../../../components/calculator/NumberInput';
import PercentInput from '../../../components/calculator/PercentInput';
import BigResult from '../../../components/calculator/BigResult';
import EmailCapture from '../../../components/calculator/EmailCapture';
import CtaBlock from '../../../components/calculator/CtaBlock';
import FaqSection from '../../../components/calculator/FaqSection';
import RelatedCalculators from '../../../components/calculator/RelatedCalculators';

const SLUG = 'ai-receptionist-roi';
const CANONICAL = `https://floridaaimarketingexperts.com/calculator/${SLUG}`;

// Canonical pricing: T5 setup $1,497 + T7 $297/mo
const AI_RECEPTIONIST_SETUP = 1497;
const AI_RECEPTIONIST_MONTHLY = 297;

export default function AiReceptionistRoiCalculatorPage() {
  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'AI Receptionist ROI Calculator',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any (Web)',
    url: CANONICAL,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description:
      'Free AI receptionist ROI calculator. See your payback period and ROI multiple before you invest.',
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is a good ROI for an AI receptionist?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most Florida businesses see a 5x-15x ROI on an AI receptionist in the first year. Payback typically lands between 7 and 30 days once the AI starts answering after-hours and overflow calls.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I calculate AI receptionist ROI?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Take additional revenue captured from calls the AI answers that humans would have missed, subtract the cost of the AI system, and divide by the cost of the AI system. Our calculator does this automatically from your current revenue, labor cost, and answer rate.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does an AI receptionist replace my office staff?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'It does not have to. Most clients use AI as a 24/7 overflow handler — it catches calls humans miss, books routine appointments, and escalates anything complex. Your staff still handles high-touch conversations.',
        },
      },
    ],
  };

  usePageTitle({
    title: 'AI Receptionist ROI Calculator | FAME',
    description:
      'Free AI receptionist ROI calculator. See payback period in days and ROI multiple before you invest. Built for Florida businesses.',
    keywords:
      'AI receptionist ROI calculator, virtual receptionist return on investment, 24/7 call answering roi, voice AI ROI',
    canonical: CANONICAL,
    schema: [softwareSchema, faqSchema],
  });

  const [monthlyRevenue, setMonthlyRevenue] = useState(80000);
  const [humanAnswerPct, setHumanAnswerPct] = useState(70);
  const [laborCost, setLaborCost] = useState(3500);

  const results = useMemo(() => {
    const missedPct = Math.max(0, 100 - humanAnswerPct) / 100;
    const missedRevenueOpportunity =
      humanAnswerPct > 0
        ? monthlyRevenue * (missedPct / (humanAnswerPct / 100))
        : monthlyRevenue;
    const aiRecoveredRevenue = missedRevenueOpportunity * 0.8;

    const annualNewRevenue = aiRecoveredRevenue * 12;
    const annualAiCost = AI_RECEPTIONIST_SETUP + AI_RECEPTIONIST_MONTHLY * 12;
    const netAnnualGain = annualNewRevenue - annualAiCost;
    const roiMultiple = annualAiCost > 0 ? annualNewRevenue / annualAiCost : 0;

    const dailyRecovery = aiRecoveredRevenue / 30;
    const paybackDays =
      dailyRecovery > 0
        ? (AI_RECEPTIONIST_SETUP + AI_RECEPTIONIST_MONTHLY) / dailyRecovery
        : 0;
    const laborSavings = laborCost * 0.3 * 12;
    return {
      aiRecoveredRevenue,
      annualNewRevenue,
      annualAiCost,
      netAnnualGain,
      roiMultiple,
      paybackDays,
      laborSavings,
    };
  }, [monthlyRevenue, humanAnswerPct, laborCost]);

  const faqs = [
    {
      question: 'What inputs do I need to calculate AI receptionist ROI?',
      answer:
        'Three numbers: your current monthly revenue, the percent of inbound calls your humans currently answer live, and your monthly labor cost on phones. If you do not know the exact answer rate, 60-75% is a realistic default for most service businesses.',
    },
    {
      question: 'Why is payback period measured in days?',
      answer:
        'Because service business sales cycles are fast. One booked enterprise install or recurring retainer can cover an entire year of AI service. Most clients pay back the $1,497 setup in under 30 days from recovered calls alone.',
    },
    {
      question: 'What if I have a dispatcher or receptionist already?',
      answer:
        'Great — the AI does not replace them. It handles overflow when they are on another line, after-hours, and on weekends. You typically reclaim 20-40% of their time for revenue-generating tasks like following up on estimates.',
    },
  ];

  return (
    <CalculatorLayout>
      <section className="relative overflow-hidden bg-white grid-bg py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <span className="eyebrow mb-5">
            <i className="ri-robot-2-line mr-2"></i>Free Calculator · Business-Specific Math
          </span>
          <h1 className="h-display text-4xl md:text-6xl mb-5 leading-[0.95] tracking-[-0.02em]">
            AI Receptionist ROI Calculator
          </h1>
          <p className="text-lg md:text-xl text-slate-600 font-body font-light max-w-3xl mx-auto">
            See your payback period and first-year ROI multiple for an AI receptionist — before
            you spend a dollar.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="fame-card p-8 shadow-xl">
              <h2 className="text-navy-900 text-2xl font-display font-bold mb-6">Your Numbers</h2>
              <div className="flex flex-col gap-6">
                <NumberInput
                  label="Current monthly revenue"
                  value={monthlyRevenue}
                  onChange={setMonthlyRevenue}
                  prefix="$"
                  step={1000}
                />
                <PercentInput
                  label="% of inbound calls answered by humans"
                  value={humanAnswerPct}
                  onChange={setHumanAnswerPct}
                  helperText="Rough answer: 60-75% for most businesses."
                />
                <NumberInput
                  label="Current phone labor cost / month"
                  value={laborCost}
                  onChange={setLaborCost}
                  prefix="$"
                  step={100}
                  helperText="Dispatcher / office / answering service combined."
                />
              </div>
              <div className="mt-6 p-4 bg-teal-50 rounded-lg text-sm text-navy-700">
                <strong>Service cost (canonical):</strong> $1,497 setup + $297/mo = $5,061 year one.
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <BigResult
                label="First-Year ROI Multiple"
                value={results.roiMultiple}
                format="multiplier"
                accent="green"
                sublabel="Every $1 invested comes back this many times."
              />
              <BigResult
                label="Payback Period"
                value={results.paybackDays}
                format="days"
                accent="teal"
                size="md"
                sublabel="Days until recovered revenue pays off the install."
              />
              <BigResult
                label="Net Annual Gain"
                value={results.netAnnualGain}
                format="currency"
                accent="navy"
                size="md"
                sublabel="After all AI receptionist costs are subtracted."
              />

              <CtaBlock
                variant="compact"
                eyebrow="Ready when you are"
                headline="Voice AI Setup · $1,497 + $297/mo"
                subheadline="Live in 7 days. Answers every call 24/7. Books deals automatically."
                primaryLabel="Start Setup"
                primaryHref={STRIPE_LINKS.T5_RECEPTIONIST_SETUP}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-navy-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-navy-900 text-2xl md:text-3xl font-display font-bold mb-4">
            How to actually measure AI receptionist ROI for a Florida business
          </h2>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            Most ROI calculators assume a corporate office environment. That is useless for a home
            services contractor, a local brand, or a professional services firm. The real ROI
            for any phone-driven Florida business comes from two buckets:{' '}
            <strong>revenue captured from calls your humans miss</strong> and{' '}
            <strong>labor offloaded from the calls your humans answer</strong>.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed mb-4">
            Our calculator uses a simple, conservative formula: if your humans answer 70% of calls
            and produce $80K/month from those calls, the 30% they miss represents about $34K/month
            of lost opportunity. An AI receptionist typically recovers 80% of that — roughly
            $27K/month in net new revenue. That alone is a 5x return on the $5,061 first-year
            cost, and it does not count labor time saved.
          </p>
          <p className="text-navy-600 text-base md:text-lg leading-relaxed">
            Payback-in-days matters most. A single recovered high-ticket deal often covers your
            entire first year of AI service. If your payback is longer than 60 days, call us —
            something is off in your inputs or there is a bigger leak upstream than the phone.
          </p>
        </div>
      </section>

      <FaqSection items={faqs} />

      <CtaBlock
        eyebrow="Your numbers, built into a real system"
        headline="Install the AI Receptionist in 7 Days"
        subheadline="$1,497 one-time setup + $297/mo. Or roll it into a T10 Starter Retainer for one integrated team."
        primaryLabel="Start Setup · $1,497"
        primaryHref={STRIPE_LINKS.T5_RECEPTIONIST_SETUP}
        secondaryLabel="Starter Retainer · $2,500/mo"
        secondaryHref={STRIPE_LINKS.T10_STARTER}
      />

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <EmailCapture
            slug={SLUG}
            calculatorInputs={{ monthlyRevenue, humanAnswerPct, laborCost }}
            calculatorResults={{
              roiMultiple: Number(results.roiMultiple.toFixed(2)),
              paybackDays: Math.round(results.paybackDays),
              netAnnualGain: Math.round(results.netAnnualGain),
            }}
          />
        </div>
      </section>

      <RelatedCalculators excludeSlug={SLUG} />
    </CalculatorLayout>
  );
}
